<tp-form [formGroup]="form" [isSubmitting$]="isSubmitting$" (doSubmit)="submit()">
  <div class="update-form">
    <h1>Edit Cluster</h1>
    <div class="form-input-row">
      <div class="form-input-row-description"><p>Compute</p></div>
      <mat-form-field class="form-input-row-content" [title]="machineTypeFieldTitle$ | async">
        <mat-select class="machine-type-mat-select" [formControl]="form.controls.machineType">
          <mat-select-trigger class="machine-type-mat-select-trigger" [class.machine-type-free]="form.value.machineType?.isFree">
            @if (form.value.machineType; as machineType) {
              <span>{{ machineType | machineType }}</span>
              @if (machineType.isBurstable) {
                <i [matTooltip]="burstableTooltip" class="burstable-indicator fa-solid fa-circle-info"></i>
              }
            }
          </mat-select-trigger>
          @for (machineType of machineTypes; track machineType) {
            <mat-option [value]="machineType.value" [class.machine-type-free]="machineType.value.isFree">
              <span>{{ machineType.viewValue }}</span>
              @if (machineType.value.isBurstable) {
                <i [matTooltip]="burstableTooltip" class="burstable-indicator fa-solid fa-circle-info"></i>
              }
            </mat-option>
          }
        </mat-select>
        @if (form.controls.machineType.getError("errorText")) {
          <mat-error>{{ form.controls.machineType.getError("errorText") }}</mat-error>
        }
      </mat-form-field>
    </div>
    <div class="form-input-row form-slider-row">
      <div class="form-input-row-description"><p>Storage</p></div>
      <div class="form-slider-field form-input-row-content">
        <mat-form-field class="form-slider-field-text-box">
          <input
            matInput type="text" readonly
            [value]="storageSliderCurrentValueToString"
          />
        </mat-form-field>
        <mat-slider min="0" max="9" [showTickMarks]="true" [displayWith]="storageSliderValueToString">
          <input [formControl]="form.controls.storageSlider" matSliderThumb>
        </mat-slider>
      </div>
    </div>
    @if (form.controls.storageSlider.getError("min")) {
      <mat-error>Storage size cannot be reduced</mat-error>
    }

    <div class="form-input-row">
      <div class="form-input-row-description"><p>Backup frequency</p></div>
      <tp-form-select class="form-input-row-content" [form]="form" field="snapshotFrequency" [options]="frequencyOptions"/>
    </div>
    @if (frequencyDisabled$ | async) {
      <div class="content-wrapper">
        <i class="fa-solid fa-circle-info"></i>
        <p>Existing automated snapshots will be deleted after they pass the retention duration</p>
      </div>
    }
    @if (showRetention$ | async) {
      <div class="form-input-row">
        <div class="form-input-row-description"><p>Backup retention</p></div>
        <tp-form-select class="form-input-row-content" [form]="form" field="snapshotRetentionDays" [options]="(retentionOptions$ | async)!"/>
      </div>
      @if (reducedRetention$ | async; as reducedRetention) {
        <div class="content-wrapper">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <p>Making this change will delete existing automated snapshots older than {{ reducedRetention }} days</p>
        </div>
      }
    }
  </div>
  <div class="dc-basket-bar">
    <div class="subtitle subtitle-first-of-type"><span>Capacity</span> <div class="flex-spacer"></div> <tp-modal-close-button /></div>
    <table>
      <tr>
        <th>Machine Type</th>
        <td><span [ngClass]="{ changed: machineTypeValueChanged, tooltip: machineTypeValueChanged }" [matTooltip]="machineTypeValueChanged ? 'Previously ' + data.cluster.machineType.vendorId : ''">
          {{ form.value.machineType?.vendorId || "--" }}
        </span></td>
      </tr>
      <tr>
        <th>Compute</th>
        <td><span [ngClass]="{ changed: machineTypeValueChanged, tooltip: machineTypeValueChanged }" [matTooltip]="machineTypeValueChanged ? 'Previously ' + previousComputeString : ''">
          {{ form.value.machineType ? (form.value.machineType! | machineType:"compute") : "--" }}
          @if (form.value.machineType?.isBurstable) {
            <i [matTooltip]="burstableTooltip" class="burstable-indicator fa-solid fa-circle-info"></i>
          }
        </span></td>
      </tr>
      <tr>
        <th>Storage</th>
        <td><span [ngClass]="{ changed: storageSizeChanged, tooltip: storageSizeChanged }" [matTooltip]="storageSizeChanged ? 'Previously ' + previousStorageString : ''">
          {{ storageSliderCurrentValueToString }}
        </span></td>
      </tr>
      <tr>
        <th>Backup frequency</th>
        <td><span [ngClass]="{ changed: snapshotFrequencyChanged, tooltip: snapshotFrequencyChanged }" [matTooltip]="snapshotFrequencyChanged ? 'Previously ' + data.cluster.snapshotConfiguration.frequency : ''">
          {{ toSentenceCase(form.value.snapshotFrequency!) }}
        </span></td>
      </tr>
      @if (showRetention$ | async) {
        <tr>
          <th>Backup retention</th>
          <td><span
            [ngClass]="{ changed: snapshotRetentionChanged && !this.data.cluster.isFree, tooltip: snapshotRetentionChanged && !this.data.cluster.isFree }"
            [matTooltip]="snapshotRetentionChanged && !this.data.cluster.isFree ? 'Previously ' + previousRetentionString : ''"
          >{{ form.value.snapshotRetentionDays }} days</span></td>
        </tr>
      }
    </table>

    <div class="subtitle"><span>Cost</span></div>
    <table>
      <tr>
        <th class="tooltip" matTooltip="Hourly rate while the cluster is running">Hourly rate</th>
        <td>
          @if (pricePerHourString$ | async; as pricePerHourString) {
            @if (basePricePerHourString$ | async; as basePricePerHourString) {
              <span class="tp-base-price">{{ basePricePerHourString }}</span>
            }
            <span [ngClass]="{ highlight: (isFree$ | async) || !!(basePricePerHourString$ | async), changed: (hourlyPriceTooltip$ | async), tooltip: (hourlyPriceTooltip$ | async) }" [matTooltip]="hourlyPriceTooltip$ | async">
              {{ pricePerHourString }}
            </span>
          } @else {
            <tp-spinner [size]="16"/>
          }
        </td>
      </tr>
<!--      @if (snapshotPriceString$ | async; as snapshotPriceString) {-->
<!--        <tr>-->
<!--          <th class="tooltip" matTooltip="Monthly cost per GB of stored snapshots">Snapshot cost</th>-->
<!--          <td>-->
<!--            @if (baseSnapshotPriceString$ | async; as baseSnapshotPriceString) {-->
<!--              <span class="tp-base-price">{{ baseSnapshotPriceString }}</span>-->
<!--            }-->
<!--            <span [ngClass]="{ highlight: (isFree$ | async) || !!(baseSnapshotPriceString$ | async) }">{{ snapshotPriceString }}/GB mo</span>-->
<!--          </td>-->
<!--        </tr>-->
<!--      }-->
    </table>
    <button type="button" class="show-more" id="showMonthlyEstimate" (click)="checkoutShowMonthlyEstimate = !checkoutShowMonthlyEstimate">
      @if (checkoutShowMonthlyEstimate) {
        Estimate <i class="fa-regular fa-chevron-up"></i>
      } @else {
        Estimate <i class="fa-regular fa-chevron-down"></i>
      }
    </button>
    <table>
      @if (checkoutShowMonthlyEstimate) {
        <tr>
          <th class="tooltip" matTooltip="Projected monthly bill, based on 100% cluster uptime over a 30-day period">Monthly projection</th>
          <td>
            @if (pricePerMonthString$ | async; as pricePerMonthString) {
              <span [ngClass]="{ highlight: (isFree$ | async), changed: (monthlyPriceTooltip$ | async), tooltip: (monthlyPriceTooltip$ | async) }" [matTooltip]="monthlyPriceTooltip$ | async">
                {{ pricePerMonthString }}
              </span>
            } @else {
              <tp-spinner [size]="16"/>
            }
          </td>
        </tr>
      }
    </table>

    @if (mustAddPaymentMethod$ | async) {
      <tp-button
        class="continue-button" buttonStyle="primary-outline green stroke"
        [enabled]="formValuesChanged && !(isSubmitting$ | async)"
        buttonId="addPaymentMethod" (click)="openPaymentDetailsDialog()">
        @if (isSubmitting$ | async) {<tp-spinner [size]="20"/>} @else {Add payment method}
      </tp-button>
    } @else {
      <tp-button
        class="continue-button"
        type="submit" [enabled]="formValuesChanged && !(isSubmitting$ | async)" buttonId="update"
        buttonStyle="primary-outline green stroke"
      >
        @if (isSubmitting$ | async) {<tp-spinner [size]="20"/>} @else {Confirm changes}
      </tp-button>
    }
  </div>
</tp-form>
@if (isSubmitting$ | async) {
  <mat-progress-bar mode="indeterminate" />
}
